<template>
  <div>
    <page-header
      icon="mdi-chart-box-outline"
      :title="$t('routes.report')"
      :subtitle="$t('manage_meal_reports')"
    >
      <template #actions v-if="!device.isMobile">
        <v-row no-gutters>
          <print-button @click="setPrinter()" class="mr-2" />
          <excel-button @click="setExcel()" class="mr-2" />
          <v-tooltip bottom v-if="date[0] == date[1] && selectedView != 2">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <primary-button @click="setPackageTab">
                  <v-icon class="mr-2">mdi-plus-circle</v-icon>
                  <span v-if="windowSize.x > 1023">{{ $t("add_meals2") }}</span>
                  <span v-else>{{ $t("add") }}</span>
                </primary-button>
              </div>
            </template>
            <span>{{ $t("add_additional_meal") }}</span>
          </v-tooltip>
        </v-row>
      </template>
    </page-header>

    <page-toolbar v-model="search" :searchable="false">
      <template #filters>
        <v-col cols="6" lg="auto">
          <date-picker v-model="date" color="white" />
        </v-col>

        <v-col cols="6" lg="auto">
          <dropdown
            v-model="selectedView"
            :items="[
              { name: $t('meal_in_meals'), id: 1 },
              { name: $t('meal_in_groops'), id: 2 },
              { name: $t('companies_fed_by_restaurant'), id: 3 },
            ]"
            :label="$t('view')"
            item-text="name"
            item-value="id"
            color="white"
            hide-details
          />
        </v-col>
        <v-col cols="6" lg="auto">
          <text-field
            v-model="search"
            kind="search"
            :label="$t('search')"
            color="white"
            hide-details
          />
        </v-col>

        <v-col cols="auto" v-if="!device.isMobile">
          <circle-button
            @click="$store.commit('report/SET_RELOAD')"
            color="blue"
            icon="mdi-reload"
            :tooltip="$t('tooltip_refresh_report')"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-2 mt-2'">
      <!-- JELA IZ GRUPE -->
      <report-tab-group-meals v-if="selectedView === 2"></report-tab-group-meals>

      <report-tab-meals :active-tab="tab" v-if="selectedView === 1"></report-tab-meals>

      <report-tab-more-restaurants
        v-if="selectedView === 3"
        :active-tab="tab"
      ></report-tab-more-restaurants>
    </div>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Add Meals Button -->
      <v-btn
        v-if="date[0] == date[1] && selectedView != 2"
        fab
        small
        color="success"
        @click="setPackageTab"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="setExcel()">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="blue-grey" dark @click="setPrinter()">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Reload Button -->
      <v-btn fab small color="blue" dark @click="$store.commit('report/SET_RELOAD')">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import ReportTabGroupMeals from "@/components/report/tabs/ReportTabGroupMeals.vue";
import ReportTabMeals from "@/components/report/tabs/ReportTabMeals.vue";
import ReportTabMoreRestaurants from "@/components/report/tabs/ReportTabMoreRestaurants.vue";
import companyService from "@/services/company";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";

export default {
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    ReportTabGroupMeals,
    ReportTabMeals,
    ReportTabMoreRestaurants,
    PageHeader,
    PageToolbar,
  },
  data() {
    return {
      fabMenu: false,
      windowSize: {
        x: 0,
        y: 0,
      },
      search: "",
      date: [new Date().toISOString().substr(0, 10)],
      loading: {
        companies: false,
      },
      companies: [],
      meals: [],
      tab: null,
      selectedView: null,
    };
  },
  watch: {
    search() {
      this.$store.commit("report/SET_FILTER", {
        key: "search",
        value: this.search,
      });
    },
    selectedView() {
      if (this.selectedView) {
        this.load();
        this.changeView();
      }
    },
    date() {
      this.$store.commit("report/SET_DATE_RANGE", {
        from: this.date[0],
        to: this.date[1],
      });
    },
  },
  methods: {
    async loadCompanies() {
      this.loading.companies = true;
      try {
        const res = await companyService.getCompanies();
        const companies = res.data || [];

        // Count companies by type
        const groupCount = companies.filter((c) => c.type === "group").length;
        const nonGroupCount = companies.length - groupCount;

        // Set default layout based on majority type
        if (groupCount > nonGroupCount) {
          this.selectedView = 2; // meal_in_groops
        } else {
          this.selectedView = 1; // meal_in_meals
        }
      } catch (error) {
        console.error("Error loading companies:", error);
        // Set default on error
        this.selectedView = 1;
      } finally {
        this.loading.companies = false;
      }
    },
    setPackageTab() {
      this.tab = 4;
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    changeView() {
      this.load();
    },
    load() {
      this.meals = "";
      if (this.date && this.date.length == 1) {
        this.date.push(this.date[0]);
      }
      if (this.date && this.date[0] > this.date[1])
        [this.date[0], this.date[1]] = [this.date[1], this.date[0]];
      if (this.$store.state.restaurant) {
        this.$store.dispatch("report/fetchFoodCompanies", {
          date: this.localeDate(this.date[0], { format: "iso" }),
          date2: this.localeDate(this.date[1], { format: "iso" }),
          restaurant_id: this.$store.state.restaurant.id,
        });
      }
    },
  },
  mounted() {
    this.onResize();
  },
  created() {
    this.loadCompanies();
  },
};
</script>
